import type { Texts, TrackStatusKey, WarehouseAddress, ShippingAddress, Shipment } from "@/type";

/** track_status 与进度条颜色、多语言描述键的映射 */
export const TRACK_STATUS_COLOR_KEY: Record<number, TrackStatusKey> = {
  1: "ordered",
  2: "shipped",
  3: "info_received",
  4: "in_transit",
  5: "out_for_delivery",
  6: "delivered",
  7: "alert",
  8: "undelivered",
  9: "expired",
  10: "pickup",
};

const TRACK_STATUS_DESC_KEY: Record<number, keyof Texts> = {
  1: "pending_status_desc",
  3: "info_received_status_desc",
  4: "in_transit_status_desc",
  5: "out_for_delivery_status_desc",
  6: "delivered_status_desc",
  7: "alert_status_desc",
  8: "undelivered_status_desc",
  9: "expired_status_desc",
  10: "pick_up_status_desc",
};

export function getTrackStatusKey(trackStatus: number): TrackStatusKey {
  return TRACK_STATUS_COLOR_KEY[trackStatus] || "ordered";
}

/** 有订单但尚未添加物流（tracking_number 为空） */
export function isTrackingNotAdded(shipment: Shipment): boolean {
  return !shipment.shipping_info?.tracking_number?.trim();
}

/** 优先使用接口返回的 status_desc，否则按 track_status 从多语言取描述 */
export function getShipmentStatusDesc(
  texts: Texts,
  trackStatus: number,
  statusDesc?: string,
): string {
  if (statusDesc?.trim()) return statusDesc;
  const key = TRACK_STATUS_DESC_KEY[trackStatus];
  return key ? texts[key] : "";
}

export function formatTimestamp(ts?: number): string {
  if (!ts) return "";
  const d = new Date(ts * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

type AddressLike = WarehouseAddress | ShippingAddress;

/** 将地址格式化为多行展示（addr 为 null 时返回空数组） */
export function formatAddressLines(addr: AddressLike | null): string[] {
  if (!addr) return [];
  const lines: string[] = [];
  if (addr.name) lines.push(addr.name);
  if (addr.address1) lines.push(addr.address1);
  const cityLine = [addr.city, addr.province, addr.zip].filter(Boolean).join(" ");
  if (cityLine) lines.push(cityLine);
  if (addr.country) lines.push(addr.country);
  return lines;
}

/** 判断地址是否有可展示内容（后端可能返回 null） */
export function hasAddressLines(addr: AddressLike | null): boolean {
  return formatAddressLines(addr).length > 0;
}

/** 判断收货地址是否有可展示内容 */
export function hasShippingAddress(addr: ShippingAddress | null): boolean {
  return hasAddressLines(addr);
}

/** 判断仓库地址是否有可展示内容 */
export function hasWarehouseAddress(addr: WarehouseAddress | null): boolean {
  return hasAddressLines(addr);
}
